import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router";
import { apiFetch } from "@/lib/api-client";
import { WebinarSurveyPanel } from "@/components/webinar/webinar-survey-panel";

/**
 * G.4: Webinar Waiting Room
 * Shown to attendees before a webinar goes live.
 * - Countdown timer (synced to scheduledAt)
 * - Custom content (waitingRoomHtml from tenant config)
 * - Auto-redirect when status changes to "live"
 * - Attendee count display (via polling — WebSocket in G.5)
 */
export default function WaitingRoomPage() {
  const { webinarId } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState<string>("loading");
  const [scheduledAt, setScheduledAt] = useState<string | null>(null);
  const [waitingRoomHtml, setWaitingRoomHtml] = useState<string>("");
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });

  // Poll webinar status every 5 seconds
  useEffect(() => {
    if (!webinarId) return;

    const checkStatus = async () => {
      try {
        const data = await apiFetch<{
          status: string;
          scheduledAt: string | null;
          waitingRoomHtml: string | null;
        }>(`/v1/webinars/${webinarId}/status`);
        setStatus(data.status);
        setScheduledAt(data.scheduledAt);
        if (data.waitingRoomHtml) setWaitingRoomHtml(data.waitingRoomHtml);

        if (data.status === "live") {
          navigate(`/webinar/${webinarId}/watch`);
        }
      } catch {
        // Silently retry on next interval
      }
    };

    checkStatus();
    const interval = setInterval(checkStatus, 5000);
    return () => clearInterval(interval);
  }, [webinarId, navigate]);

  // Countdown timer
  useEffect(() => {
    if (!scheduledAt) return;

    const updateCountdown = () => {
      const diff = new Date(scheduledAt).getTime() - Date.now();
      if (diff <= 0) {
        setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }
      setCountdown({
        days: Math.floor(diff / (1000 * 60 * 60 * 24)),
        hours: Math.floor((diff / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((diff / (1000 * 60)) % 60),
        seconds: Math.floor((diff / 1000) % 60),
      });
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000);
    return () => clearInterval(interval);
  }, [scheduledAt]);

  if (status === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (status === "ended" || status === "cancelled") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-foreground">Webinar has ended</h1>
          <p className="mt-2 text-muted-foreground">Check your email for the replay link.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background px-4">
      <div className="w-full max-w-lg text-center">
        <h1 className="mb-2 text-3xl font-bold text-foreground">Starting Soon</h1>

        {/* Countdown */}
        <div className="my-8 flex justify-center gap-4">
          {Object.entries(countdown).map(([unit, value]) => (
            <div key={unit} className="text-center">
              <div className="rounded-lg bg-card px-4 py-3 text-3xl font-bold text-foreground shadow-sm">
                {String(value).padStart(2, "0")}
              </div>
              <span className="mt-1 text-xs uppercase text-muted-foreground">{unit}</span>
            </div>
          ))}
        </div>

        {/* Custom content from tenant — rendered as text, not raw HTML (XSS prevention) */}
        {waitingRoomHtml && (
          <div className="mx-auto mb-8 max-w-md rounded-lg border border-border bg-card p-6 text-sm text-foreground">
            {waitingRoomHtml}
          </div>
        )}

        <p className="text-sm text-muted-foreground">
          You'll be automatically redirected when the webinar starts.
        </p>

        <div className="mt-8 text-left">
          <WebinarSurveyPanel
            webinarId={webinarId}
            displayAt="pre"
            title="Pre-webinar check-in"
            emptyLabel="No pre-webinar survey is active right now."
          />
        </div>
      </div>
    </div>
  );
}
